/**
 * USPR (US State Privacy Regulations) consent override.
 *
 * `.ccpa` is an object (live-verified on a real USPR `preference` argument:
 * `{ uspString: '1YN-', firstAcknowledgeDate: '...', optOutDate: null }`) -
 * NOT a bare string (an earlier version of this function assumed that,
 * which meant it always returned null - silently inert) and NOT the `.uspr`
 * object this module originally assumed either. It only appears once a
 * consent decision has been recorded (absent before that).
 *
 * `uspString` is an IAB "US Privacy String" (4 chars): "1" (spec version) +
 * ExplicitNotice(Y/N/-) + OptedOutOfSale(Y/N/-) + LSPACoveredTransaction
 * (Y/N/-). "-" at the notice position is the standard "not applicable"
 * placeholder - i.e. this visitor isn't in a region the framework covers -
 * so CH/DE/AT sessions are expected to either omit `ccpa` entirely or carry
 * "-" there; either way this function returns `null` for them, same as
 * before. `optOutDate` (null until the visitor opts out, then a timestamp)
 * is used for the actual opt-out signal instead of re-parsing the string.
 *
 * @param {object} [source] The object to read `.ccpa` from. Pass the
 *   `preference` argument iubenda hands to the `onPreferenceExpressedOrNotNeeded`
 *   callback when calling from there - `window._iub.cs.consent` may not be
 *   populated yet at the exact moment the callback fires (live-verified:
 *   read from the global came back empty even though the visible `ccpa`
 *   value logged moments later, via a manual console check, was correct -
 *   the existing GDPR handling avoids this by also reading from the
 *   callback's `preference` argument, never the global, for the same
 *   reason). Defaults to the global for call sites outside the callback
 *   (e.g. getCookieConsent(), _getConsent()), where no `preference` exists.
 * @returns {boolean|null} `null` when this is not (yet, or ever) a USPR
 *   session (callers should fall back to the GDPR `purposes` handling,
 *   unchanged), otherwise `true`/`false` for granted/opted-out-of-sale.
 */
const US_PRIVACY_STRING_PATTERN = /^1[YN-]{3}$/

export function getUsprConsentOverride(source = window._iub?.cs?.consent) {
  const ccpa = source?.ccpa
  const uspString = ccpa?.uspString
  if (typeof uspString !== 'string' || !US_PRIVACY_STRING_PATTERN.test(uspString)) {
    return null
  }

  const noticeGiven = uspString[1]
  if (noticeGiven === '-') {
    return null
  }

  return !ccpa.optOutDate
}
